import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FilterMatchMode } from 'primereact/api';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Paginator } from 'primereact/paginator';
import { ProgressBar } from 'primereact/progressbar';
import { ScrollTop } from 'primereact/scrolltop';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useAuth } from '../contexts/AuthContext.js';
import useStatusPageScript from '../hooks/useStatusPageScript.js';
import Login from './Login.js';
import supportClientStatusLogsApi from '../api/supportClientStatusLogsApi.js';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200].map((value) => ({ label: String(value), value }));

const toIso = (value) => {
  if (!value) return undefined;
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return undefined;
  return dt.toISOString();
};

const formatTimeLocal = (value) => {
  if (!value) return '-';
  let dt = new Date(value);
  if (Number.isNaN(dt.getTime()) && typeof value === 'string') {
    dt = new Date(`${value}Z`);
  }
  if (Number.isNaN(dt.getTime())) {
    return String(value);
  }
  return dt.toLocaleString('en-GB');
};

const getStatusSeverity = (row) => {
  switch (row.status) {
    case 'Online':
      return 'success';
    case 'Offline':
      return 'danger';
    default:
      return null;
  }
};

const statusBodyTemplate = (row) => {
  return <Tag value={String(row.status)} severity={getStatusSeverity(row)}></Tag>;
};

const SupportClientStatusLog = () => {
  useStatusPageScript();

  const navigate = useNavigate();
  const { logout } = useAuth();

  const toast = useRef(null);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');

  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.CONTAINS },
    timeFormatted: { value: null, matchMode: FilterMatchMode.CONTAINS },
    'client.name': { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [globalFilterValue, setGlobalFilterValue] = useState('');

  const [dateRange, setDateRange] = useState([new Date(), new Date()]);

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);

  const [showLoginModal, setShowLoginModal] = useState(false);

  const progressbar = (
    <div className="card mt-3">
      <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
    </div>
  );

  const getContextInputs = () => {
    return {
      customerId: localStorage.getItem('supportclientstatus_customerid') || localStorage.getItem('id') || '',
      locationName: localStorage.getItem('supportclientstatus_locationname') || localStorage.getItem('supportsite') || '',
      clientName: localStorage.getItem('supportclientstatus_clientname') || localStorage.getItem('supportclient') || '',
    };
  };

  const hasRequiredInputs = () => {
    const ctx = getContextInputs();
    return Boolean(String(ctx.customerId).trim() && String(ctx.locationName).trim() && String(ctx.clientName).trim());
  };

  const getTotalRecordsFromPage = (page, pageSize, itemsLength) => {
    if (itemsLength >= pageSize) {
      return (page + 1) * pageSize + 1;
    }
    return page * pageSize + itemsLength;
  };

  const buildRequestPayload = (page, pageSize) => {
    const ctx = getContextInputs();
    const [start, end] = Array.isArray(dateRange) ? dateRange : [];
    const startDate = start || null;
    const endDate = end || startDate;

    return {
      customerId: Number(ctx.customerId),
      locationName: String(ctx.locationName).trim(),
      clientName: String(ctx.clientName).trim(),
      query: {
        initDate: toIso(startDate),
        endDate: toIso(endDate),
        page,
        pageSize,
      },
    };
  };

  const fetchStatusLogs = async (page, pageSize) => {
    if (!hasRequiredInputs()) {
      setInfoMessage('Select a support client first, then open status logs.');
      setEvents([]);
      setTotalRecords(0);
      return;
    }

    try {
      setLoading(true);
      setErrorMessage('');
      setInfoMessage('');

      const ctx = getContextInputs();
      localStorage.setItem('supportclientstatus_customerid', String(ctx.customerId));
      localStorage.setItem('supportclientstatus_locationname', String(ctx.locationName));
      localStorage.setItem('supportclientstatus_clientname', String(ctx.clientName));

      const payload = buildRequestPayload(page, pageSize);
      const appid = localStorage.getItem('appid');
      const response = await supportClientStatusLogsApi.getSupportClientStatusLogs(payload, appid);

      const list = (Array.isArray(response) ? response : []).map((item) => ({
        ...item,
        timeFormatted: formatTimeLocal(item.time),
      }));

      setEvents(list);
      setTotalRecords(getTotalRecordsFromPage(page, pageSize, list.length));
    } catch (error) {
      console.error('Error fetching support client status logs:', error);
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        setShowLoginModal(true);
      }
      setEvents([]);
      setTotalRecords(0);
      setErrorMessage('Failed to load client status logs. Please try again.');
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: '', detail: 'Failed to load client status logs. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasRequiredInputs()) {
      fetchStatusLogs(0, rows);
    } else {
      setInfoMessage('Select a support client first, then open status logs.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    const nextFilters = { ...filters };
    nextFilters.global.value = value;
    setFilters(nextFilters);
    setGlobalFilterValue(value);
  };

  const onPageChange = (event) => {
    setRows(event.rows);
    setFirst(event.first);
    fetchStatusLogs(event.page, event.rows);
  };

  const onSearch = () => {
    setFirst(0);
    fetchStatusLogs(0, rows);
  };

  const onReset = () => {
    setDateRange([new Date(), new Date()]);
    setFirst(0);
    setErrorMessage('');
    setInfoMessage('');
    if (hasRequiredInputs()) {
      fetchStatusLogs(0, rows);
    }
  };

  const logoutUser = async () => {
    await logout();
    navigate('/login');
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    if (toast.current) {
      toast.current.show({ severity: 'success', summary: '', detail: 'Login successful! You can continue working.' });
    }
    fetchStatusLogs(Math.floor(first / rows), rows);
  };

  return (
    <Card className="justify-content-md-center mt-5">
      <Container fluid="md">
        <div className="panel">
          <div className="flex align-content-center flex-wrap">
            <Button link icon="pi pi-arrow-left" onClick={() => navigate(-1)} className="flex mb-3" />
            <h4 className="flex mt-2 mb-3">Support Client Status Logs</h4>

            <span className="p-input-icon-left mb-3 ml-3">
              <i className="pi pi-search" />
              <InputText
                value={globalFilterValue}
                onChange={onGlobalFilterChange}
                placeholder="Filter Search"
                tooltip="Start typing to filter"
                tooltipOptions={{ position: 'bottom' }}
              />
            </span>

            <div className="card flex justify-content-center ml-3 mb-3">
              <Calendar
                value={dateRange}
                onChange={(e) => setDateRange(e.value || [new Date(), new Date()])}
                selectionMode="range"
                dateFormat="dd/mm/yy"
                showIcon
                showButtonBar
                readOnlyInput
              />
            </div>

            <div className="card flex justify-content-center ml-3 mb-3" style={{ minWidth: '8rem' }}>
              <Dropdown
                value={rows}
                options={PAGE_SIZE_OPTIONS}
                onChange={(e) => {
                  setRows(Number(e.value));
                  setFirst(0);
                }}
                placeholder="Page size"
              />
            </div>

            <div className="card flex justify-content-center ml-3 mb-3">
              <Button label="Search" className="p-button-raised" onClick={onSearch} disabled={loading} />
            </div>

            <div className="card flex justify-content-center ml-3 mb-3">
              <Button label="Reset" className="p-button-outlined" onClick={onReset} disabled={loading} />
            </div>
          </div>
        </div>

        {loading && progressbar}
        <Toast ref={toast} />

        {infoMessage && (
          <Row className="justify-content-md-center mt-3">
            <Col>
              <Card>
                <small style={{ color: '#6c757d' }}>{infoMessage}</small>
              </Card>
            </Col>
          </Row>
        )}

        {errorMessage && (
          <Row className="justify-content-md-center mt-3">
            <Col>
              <Card>
                <div className="flex align-items-center justify-content-between">
                  <span>{errorMessage}</span>
                  <Button label="Retry" icon="pi pi-refresh" className="p-button-sm" onClick={() => fetchStatusLogs(Math.floor(first / rows), rows)} />
                </div>
              </Card>
            </Col>
          </Row>
        )}

        <Row className="justify-content-md-center mt-5">
          <Col>
            <DataTable
              value={events}
              dataKey="id"
              tableStyle={{ minWidth: '50rem' }}
              filters={filters}
              filterDisplay="menu"
              stripedRows
              globalFilterFields={['status', 'timeFormatted', 'client.name']}
              emptyMessage={loading ? 'Loading client status logs...' : 'No client status logs found for selected date range.'}
              scrollable
              scrollHeight="60vh"
            >
              <Column field="timeFormatted" header="Time" style={{ minWidth: '11rem' }}></Column>
              <Column field="status" header="Status" body={statusBodyTemplate} style={{ minWidth: '8rem' }}></Column>
              <Column field="client.name" header="Client" style={{ minWidth: '12rem' }}></Column>
            </DataTable>
          </Col>
        </Row>

        <Row className="justify-content-md-center mt-5">
          <div className="card">
            <Paginator
              first={first}
              rows={rows}
              totalRecords={totalRecords}
              rowsPerPageOptions={[25, 50, 100, 200]}
              onPageChange={onPageChange}
            />
          </div>
        </Row>

        <div className="card flex justify-content-center">
          <Dialog
            header="Session Expired - Please Login Again"
            visible={showLoginModal}
            style={{ width: '450px' }}
            modal={true}
            closable={false}
            className="justify-content-center"
          >
            <p className="mb-3">Your session has expired. Please log in again to continue working without losing your form data.</p>
            <div className="flex justify-content-center">
              <Login onLoginSuccess={handleLoginSuccess} />
            </div>
          </Dialog>
        </div>
      </Container>
      <ScrollTop />
    </Card>
  );
};

export default SupportClientStatusLog;
